import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { useQuery } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import {
  GROUP_TYPE_TYPES,
  MANAGER,
  MANAGER_CONFIG_FIELDS,
  PROVIDER,
  PROVIDER_CONFIG_FIELDS,
} from 'constants/groupTypes';
import { useAdminForm } from 'hooks/datasource';
import { RehabState } from 'models/flexible/RehabState';
import { createGroupType, groupTypesQueryKeys, showGroupType, updateGroupType } from 'services/api/admin/groupTypes';
import { indexRehabStates, rehabStatesQueryKeys } from 'services/api/rehabStates';

import { flattenTranslations, translationsForRehabState, unflattenTranslations } from './translationsHelper';
import TranslationsSlideout from './TranslationsSlideout';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const schema = yup.object({
  name: yup.string().required('Standard name is required'),
  displayName: yup.string().required('Display name is required'),
  type: yup.mixed().test('type', 'Category is required', (v: any) => !!v?.value),
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function EditGroupType() {
  const { id } = useParams();
  const isEditing = !!id;

  const form = useAdminForm({
    id,
    show: showGroupType,
    create: createGroupType,
    update: (updateId, payload) => updateGroupType({ id: updateId, ...payload }),
    include: [] as const,
    queryKey: groupTypesQueryKeys,
    schema,
    toFormValues: (data) => ({
      name: data?.name ?? '',
      displayName: data?.displayName ?? '',
      isInpatient: data?.isInpatient ?? false,
      type: (data?.type?.apiName ? (GROUP_TYPE_TYPES.find((t) => t.value === data.type.apiName) ?? null) : null) as {
        label: string;
        value: string;
      } | null,
      config: (data?.config as Record<string, boolean>) ?? {},
      translations: unflattenTranslations((data?.translations as Record<string, string>) ?? {}),
    }),
    toPayload: (v) => ({
      name: v.name ?? '',
      displayName: v.displayName ?? '',
      isInpatient: v.isInpatient ?? false,
      type: v.type?.value ?? '',
      config: v.config ?? {},
      translations: v.type?.value === PROVIDER ? flattenTranslations(v.translations ?? {}) : undefined,
    }),
    onCreateSuccess: { toast: 'Group type successfully added!', navigate: '/admin/group-types' },
    onUpdateSuccess: { toast: 'Group type successfully updated!', navigate: '/admin/group-types' },
  });

  const isManager = form.values.type?.value === MANAGER;
  const isProvider = form.values.type?.value === PROVIDER;
  const configFields = isManager ? MANAGER_CONFIG_FIELDS : PROVIDER_CONFIG_FIELDS;

  // Load rehab states for provider translation overrides
  const { data: rehabStatesData } = useQuery({
    queryKey: rehabStatesQueryKeys.index,
    queryFn: indexRehabStates,
    enabled: isProvider,
  });
  const rehabStates = rehabStatesData?.data ?? [];

  // Set default config when category changes
  useEffect(() => {
    if (isManager) {
      form.setValue('isInpatient', false);
      if (!isEditing) {
        form.setValue('config', {
          canReceiveEscalations: true,
          patientPhysicianTeamRequired: true,
          patientTransferredFromRequired: true,
        });
      }
    } else if (!isEditing) {
      form.setValue('config', {
        designatesAcceptedPlans: false,
        hideDischargeSummaryPrompt: false,
        hideDischargedAma: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager]);

  // Slideout state
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [selectedRehabState, setSelectedRehabState] = useState<RehabState | undefined>();

  if (isEditing && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title={`${isEditing ? 'Edit' : 'Add'} Group Type`} />

      <InputGroup title='Category' error={form.errors.type}>
        <Select
          options={GROUP_TYPE_TYPES}
          value={form.values.type}
          onChange={(v) => form.setValue('type', v)}
          getOptionValue={(o) => o.value}
          getOptionLabel={(o) => o.label}
          placeholder='Category'
          isDisabled={isEditing}
        />
      </InputGroup>

      <InputGroup title='Standard Name' error={form.errors.name}>
        <Input
          value={form.values.name ?? ''}
          onChange={(e) => form.setValue('name', e.target.value)}
          placeholder='Type...'
          $hasError={!!form.errors.name}
          disabled={isEditing}
        />
      </InputGroup>

      <InputGroup title='Display Name' error={form.errors.displayName}>
        <Input
          value={form.values.displayName ?? ''}
          onChange={(e) => form.setValue('displayName', e.target.value)}
          placeholder='Type...'
          $hasError={!!form.errors.displayName}
        />
      </InputGroup>

      <CheckboxSection>
        {isManager ? (
          configFields.map((field) => (
            <CheckboxRow key={field.value}>
              <input
                type='checkbox'
                checked={!!form.values.config?.[field.value]}
                onChange={(e) => form.setValue('config', { ...form.values.config, [field.value]: e.target.checked })}
              />
              <span>{field.label}</span>
            </CheckboxRow>
          ))
        ) : (
          <>
            <CheckboxRow>
              <input
                type='checkbox'
                checked={form.values.isInpatient ?? false}
                onChange={(e) => form.setValue('isInpatient', e.target.checked)}
              />
              <span>Inpatient</span>
            </CheckboxRow>
            <Divider />
            {configFields.map((field) => (
              <CheckboxRow key={field.value}>
                <input
                  type='checkbox'
                  checked={!!form.values.config?.[field.value]}
                  onChange={(e) => form.setValue('config', { ...form.values.config, [field.value]: e.target.checked })}
                />
                <span>{field.label}</span>
              </CheckboxRow>
            ))}
          </>
        )}
      </CheckboxSection>

      {isProvider && (
        <>
          <Divider />
          <SectionHeader>Stage Overrides</SectionHeader>
          {rehabStates.map((state: RehabState) => {
            const translationCount = Object.values(translationsForRehabState(state.apiName)).flat().length;
            const hasModifiedTranslations =
              form.values.translations?.[state.apiName] &&
              Object.values(form.values.translations[state.apiName]).some((v) => v && v.trim() !== '');

            return (
              <LauncherRow
                key={state.id}
                onClick={() => {
                  setSelectedRehabState(state);
                  setSlideoutOpen(true);
                }}>
                <LauncherLabel>
                  <span>{state.state}</span>
                  <LauncherSubLabel>{translationCount} fields</LauncherSubLabel>
                </LauncherLabel>
                {hasModifiedTranslations && <ModifiedPill>Modified</ModifiedPill>}
              </LauncherRow>
            );
          })}
        </>
      )}

      <ButtonGroup style={{ justifyContent: 'flex-end' }}>
        <Button variant='ghost' onClick={form.goBack}>
          Cancel
        </Button>
        <Button onClick={form.submit} disabled={!form.valid || form.submitting} loading={form.submitting}>
          {isEditing ? 'Update Group Type' : 'Create Group Type'}
        </Button>
      </ButtonGroup>

      {selectedRehabState && (
        <TranslationsSlideout
          open={slideoutOpen}
          rehabState={selectedRehabState}
          handleClose={() => setSlideoutOpen(false)}
          translations={form.values.translations ?? {}}
          onApply={(apiName, t) => form.setValue('translations', { ...(form.values.translations ?? {}), [apiName]: t })}
        />
      )}
    </AdminFormPage>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const CheckboxSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 14px;
  cursor: pointer;

  input {
    width: 16px;
    height: 16px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--black-10);
  margin: 20px 0;
`;

const LauncherRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid var(--black-10);
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;

  &:hover {
    background-color: var(--black-05);
  }
`;

const LauncherLabel = styled.div`
  display: flex;
  flex-direction: column;
`;

const LauncherSubLabel = styled.span`
  font-size: 12px;
  color: var(--black-50);
`;

const ModifiedPill = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #725a12;
  border: 1px solid #725a12;
  background-color: rgba(240, 192, 47, 0.1);
  border-radius: 12px;
  padding: 2px 8px;
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
